import { Box } from '@chakra-ui/react';
import { Editor } from '@kula-ai/components';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useField } from 'formik';
import { debounce } from 'lodash-es';
import { sanitizeHtml } from '@/lib/purify';

interface KulaFormEditorItem {
  name?: string;
  content?: string;
  editable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  pluginsConfig?: string[];
  editorStyles?: { height?: string | number };
  /**
   * Escape hatch: any supported kula-ai `Editor` prop (variable picker,
   * attachments, previewHtml, extraExtensions, toolbarFeatures overrides, a
   * different `table` profile, height caps, …) is spread straight through.
   * The Formik-critical props (defaultValue / onChange / editable / error) stay
   * owned by this wrapper and cannot be overridden.
   */
  editorProps?: Record<string, any>;
}

interface KulaFormEditorProps {
  item?: KulaFormEditorItem;
}

// Memoized so a sibling field's `setValue` (which re-renders the Formik form)
// doesn't re-render this Editor on every keystroke. All props below are
// referentially stable.
const MemoEditor = memo(Editor);

// Default toolbar mirrors the legacy FormEditor richtext config (bold, italic,
// strike, clear-format, lists, headings/text-style, alignment) collapsed into
// the "⋮" overflow. image defaults to true in kula-ai, so disable it
// explicitly. Overridable via item.editorProps.
const RICHTEXT_TOOLBAR_FEATURES = {
  textStyle: true,
  bold: true,
  italic: true,
  strike: true,
  clearFormat: true,
  list: true,
  align: true,
  overflow: true,
  image: false,
} as const;

// Stable ref so the `table` prop identity doesn't churn across renders. The
// `document` profile's own defaults handle page-fit (over-wide tables scaled to
// the US-Letter content box on import) as of @kula-ai/components 1.11.46.
const DOCUMENT_TABLE = { profile: 'document' } as const;

// The kula-ai Editor stores per-column table widths in a non-standard `colwidth`
// attribute that DOMPurify strips by default — collapsing a seeded offer/CTC
// table's columns to an equal-width fallback on load. Keep it so authored column
// widths survive the seed sanitize.
const SANITIZE_CONFIG = { ADD_ATTR: ['colwidth'] };

// How long to wait after the last keystroke before writing to Formik. Writing
// per keystroke re-renders + revalidates every field on the (large) offer form,
// which makes typing and the whole drawer feel laggy. 300ms matches the legacy
// FormEditor.
const COMMIT_DEBOUNCE_MS = 300;

// Legacy FormEditor collapses a lone empty paragraph to '' before writing to
// Formik; mirror it so required-validation and the submit payload are identical.
const isEmptyHtml = (html?: string): boolean => {
  if (!html) return true;
  const stripped = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
  return stripped.length === 0;
};

/**
 * Drop-in replacement for the legacy tiptap FormEditor, backed by the kula-ai
 * `Editor`. A thin Formik adapter: seeds the field once (uncontrolled), writes
 * HTML back on change (debounced), and passes every other prop through to
 * `Editor` via `item.editorProps`. Reads/writes the SAME Formik field as
 * FormEditor, so business logic (validation, submit payload) is unchanged.
 *
 * Kept uncontrolled + write-only: the kula Editor emits no onChange on mount, so
 * there is no echo to guard against and no value↔onChange loop.
 */
export const KulaFormEditor = ({ item }: KulaFormEditorProps = {}) => {
  const {
    name,
    content = '',
    editable = true,
    disabled = false,
    placeholder = '',
    pluginsConfig = [],
    editorStyles = {},
    editorProps = {},
  } = item || {};

  const [, meta, helpers] = useField(name ?? '');

  // Seed once — the field's value is present when it mounts (the drawer opens
  // with the offer already loaded). Sanitizing lazily (not per render) avoids a
  // full DOMPurify pass on every keystroke; the editor is uncontrolled so it
  // only reads this once.
  const seedRef = useRef<string>();
  if (seedRef.current === undefined) {
    seedRef.current = sanitizeHtml(
      String(meta.value ?? content ?? ''),
      SANITIZE_CONFIG
    );
  }

  // Keep the latest Formik helpers in a ref so the debounced writer stays a
  // single stable instance (helpers can change identity across renders).
  const helpersRef = useRef(helpers);
  helpersRef.current = helpers;

  const commit = useMemo(
    () =>
      debounce((html: string) => {
        helpersRef.current.setValue(isEmptyHtml(html) ? '' : html);
        helpersRef.current.setTouched(true, false);
      }, COMMIT_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => commit.cancel(), [commit]);

  // Flush any pending write when focus leaves the editor, so a value typed right
  // before clicking "Next"/"Save" isn't dropped by the debounce.
  const flush = useCallback(() => commit.flush(), [commit]);

  // Portal the toolbar's Radix menus into the active focus-lock (the host
  // drawer/modal) instead of document.body. A body-portaled menu is OUTSIDE the
  // trap, so opening one would move focus out and react-focus-lock would yank it
  // to the first field and scroll the dialog to the top. Pairs with the Editor's
  // on-close focus-restore (kula-ai ≥ 1.11.31). Null (no active trap) → the
  // Editor defaults to document.body.
  const boxRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );
  useEffect(() => {
    setPortalContainer(
      (boxRef.current?.closest(
        '[data-focus-lock-disabled="false"]'
      ) as HTMLElement) ?? null
    );
  }, []);

  if (!name) return null;

  const isDisabled = disabled || !editable;

  return (
    <Box ref={boxRef} onBlurCapture={flush}>
      <MemoEditor
        // Presentational defaults — a consumer's editorProps overrides these.
        placeholder={placeholder}
        toolbarFeatures={RICHTEXT_TOOLBAR_FEATURES}
        table={pluginsConfig.includes('table') ? DOCUMENT_TABLE : undefined}
        minHeight={editorStyles.height ?? '300px'}
        portalContainer={portalContainer}
        {...editorProps}
        // Formik-owned props — locked after the spread so they can't be undone.
        defaultValue={seedRef.current}
        onChange={commit}
        editable={!isDisabled}
        error={Boolean(meta.touched && meta.error)}
      />
    </Box>
  );
};
